import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  HostListener,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  tap,
  filter,
  map,
  takeUntil,
} from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { authService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

interface SearchResult {
  type: 'lead' | 'activity';
  id: number;
  title: string;
  subtitle: string;
  completed: any;
  details: {
    lead_id?: number;
    step?: string;
    status?: string;
    program?: string;
    campus?: string;
    owner?: string;
    email?: string;
    phone?: string;
    dueDate?: string;
    assignedTo?: string;
    type?: string;
  };
  created_at?: string;
  dueDate?: string;
}

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule,
    MatTooltipModule,
    MatButtonModule,
  ],
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
})
export class GlobalSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  searchControl = new FormControl<string>('');
  searchResults$!: Observable<SearchResult[]>;
  isLoading = false;
  isOpen = false;
  selectedIndex = -1;
  noResults = false;
  showNoResultsMessage = false;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  currentUserRole: string;

  recentSearches: SearchResult[] = [];
  popularSearchTerms = ['New Leads', 'Today Activities', 'Pending Tasks', 'Hot Leads'];

  constructor(
    private http: HttpClient,
    private router: Router,
    private authService: authService
  ) {
    this.currentUserRole = this.authService.getUserRole();
  }

  // Check if the user has access to a specific menu item
  hasAccess(roleStrict: string[]): boolean {
    return roleStrict.includes(this.currentUserRole);
  }

  // Display function for autocomplete
  displayFn = (_: any): string => '';

  ngOnInit() {
    this.loadRecentSearches();

    // --- SEARCH PIPELINE (drives requests) ---
    this.searchResults$ = this.searchSubject.pipe(
      map((term) => this.normalizeTerm(term)),
      debounceTime(300),
      distinctUntilChanged(), // works because we push '' when clearing/short input
      tap(() => {
        this.isLoading = true;
        this.noResults = false;
      }),
      switchMap((term) => (term.length > 2 ? this.performSearch(term) : of([]))),
      tap((results) => {
        this.isLoading = false;
        this.noResults = results.length === 0 && !!this.searchControl.value && this.searchControl.value.length > 2;
        this.selectedIndex = -1;
      }),
      catchError(() => {
        this.isLoading = false;
        this.noResults = true;
        return of([]);
      })
    );

    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        // Ensure value is a string before normalizing
        const term = this.normalizeTerm(value || '');

        this.isOpen = true;
        if (this.autocompleteTrigger) {
          this.autocompleteTrigger.openPanel();
        }

        if (term.length <= 2) {
          this.showNoResultsMessage = term.length > 0;
          this.noResults = false;
          this.isLoading = false;
          this.searchSubject.next(''); // break distinct chain
          return;
        }

        this.showNoResultsMessage = false;
        this.searchSubject.next(term);
      });
  }

  ngAfterViewInit() {
    // Autocomplete trigger initialization
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleFocus() {
    this.isOpen = true;
    const term = this.normalizeTerm(this.searchControl.value || '');
    this.showNoResultsMessage = term.length > 0 && term.length <= 2;
    // ensure the panel opens when the input gets focus
    setTimeout(() => {
      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.openPanel();
      }
    }, 0);
  }

  private normalizeTerm(term: string | null): string {
    // Ensure term is a string before using string methods
    if (typeof term !== 'string') {
      return '';
    }
    return term.trim().toLowerCase();
  }

  private performSearch(term: string): Observable<SearchResult[]> {
    const params = new HttpParams().set('searchTerm', term).set('pageSize', '10');

    return this.http.get<any>(`${environment.apiUrl}/general/global-search`, { params }).pipe(
      map((response) => {
        const list: SearchResult[] = response?.results ?? [];
        return list.map((result) => {
          if (result.type === 'activity') {
            if (result.completed === true) {
              result.details.status = 'done';
            } else if (!result.completed && result.details?.dueDate) {
              const due = new Date(result.details.dueDate);
              if (due.getTime() < Date.now()) {
                result.details.status = 'overdue';
              }
            }
          }
          return result;
        });
      }),
      catchError(() => of([]))
    );
  }

  onResultClick(result: SearchResult) {
    this.saveToRecentSearches(result);
    
    // Close search BEFORE navigation to prevent panel reopening
    this.closeSearch();
    
    // Short delay to ensure UI updates before navigation
    setTimeout(() => {
      this.navigateToResult(result);
    }, 10);
  }

  private navigateToResult(result: SearchResult) {
    if (result.type === 'lead') {
      this.router.navigate(['/leads/leads-managment/manage', result.id]);
    } else {
      this.router.navigate(['/activities/list'], {
        queryParams: { activity_id: result.id },
      });
    }
  }

  clearRecentSearches() {
    this.http.delete(`${environment.apiUrl}/general/search-history`)
      .subscribe({
        next: () => {
          this.recentSearches = [];
        },
        error: (error) => console.error('Error clearing search history:', error)
      });
  }

  private saveToRecentSearches(result: SearchResult) {
    this.http.post(`${environment.apiUrl}/general/search-history`, {
      searchResult: result
    }).subscribe({
      next: () => {
        // Refresh recent searches
        this.loadRecentSearches();
      },
      error: (error) => console.error('Error saving search:', error)
    });
  }

  private loadRecentSearches() {
    this.http.get<SearchResult[]>(`${environment.apiUrl}/general/search-history`)
      .subscribe({
        next: (searches) => {
          this.recentSearches = searches;
        },
        error: (error) => {
          console.error('Error loading search history:', error);
          this.recentSearches = [];
        }
      });
  }

  closeSearch() {
    this.isOpen = false;
    this.selectedIndex = -1;
    this.showNoResultsMessage = false;

    // Clear and ensure a distinct break so same term re-emits later
    this.searchControl.setValue('', { emitEvent: false });
    this.searchSubject.next('');

    // Close panel explicitly with a slight delay to ensure Angular's change detection has completed
    setTimeout(() => {
      if (this.autocompleteTrigger && this.autocompleteTrigger.panelOpen) {
        this.autocompleteTrigger.closePanel();
      }
      
      // Force blur the input
      if (this.searchInput && this.searchInput.nativeElement) {
        this.searchInput.nativeElement.blur();
      }
    }, 0);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.isOpen) return;

    const results = document.querySelectorAll('.search-result-item');

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, results.length - 1);
        this.scrollToSelected();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.scrollToSelected();
        break;

      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0 && results[this.selectedIndex]) {
          (results[this.selectedIndex] as HTMLElement).click();
        }
        break;

      case 'Escape':
        this.closeSearch();
        break;
    }
  }

  private scrollToSelected() {
    const results = document.querySelectorAll('.search-result-item');
    if (this.selectedIndex >= 0 && results[this.selectedIndex]) {
      (results[this.selectedIndex] as HTMLElement).scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const searchContainer = document.querySelector('.search-container');
    if (searchContainer && !searchContainer.contains(event.target as Node)) {
      this.closeSearch();
    }
  }

  getResultIcon(type: 'lead' | 'activity'): string {
    return type === 'lead' ? 'person' : 'event_note';
  }

  getStatusColor(status: string): string {
    const statusColors: { [key: string]: string } = {
      Deal: '#0a7ea4',
      Won: '#059669',
      Lost: '#dc2626',
      todo: '#f59e0b',
      done: '#10b981',
      overdue: '#ef4444',
      completed: '#10b981',
    };
    return statusColors[status] || '#6b7280';
  }

  getStatusBgColor(status: string): string {
    return this.getStatusColor(status) + '20';
  }

  getTypeColor(type: 'lead' | 'activity'): string {
    return type === 'lead' ? '#0a7ea4' : '#f59e0b';
  }

  getLeadResults(results: SearchResult[]): SearchResult[] {
    return results.filter((r) => r.type === 'lead');
  }

  getActivityResults(results: SearchResult[]): SearchResult[] {
    return results.filter((r) => r.type === 'activity');
  }

  isActivitySelected(results: SearchResult[], activityIndex: number): boolean {
    const leadCount = this.getLeadResults(results).length;
    return this.selectedIndex === leadCount + activityIndex;
  }

  onQuickSearch(term: string) {
    const normalized = this.normalizeTerm(term);
    this.searchControl.setValue(normalized, { emitEvent: true });
    this.searchInput?.nativeElement?.focus();
    // ensure request triggers even if chip equals previous typed term
    this.searchSubject.next('');
    this.searchSubject.next(normalized);
    if (this.autocompleteTrigger) this.autocompleteTrigger.openPanel();
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays < -1) return `${Math.abs(diffDays)} days ago`;
    if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
    return date.toLocaleDateString();
  }
}